import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';
import { Sidebar } from '../../components/Sidebar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import {
  ClipboardList, Plus, Loader2, Trash2, ArrowUpRight, IndianRupee, FileCheck2,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Lightweight Indian rupee formatter — falls back gracefully on null/0.
const fmtInr = (n) => (n == null || n === '' ? '—' : `₹${Number(n).toLocaleString('en-IN')}`);

export const BrandCampaignPlans = () => {
  const location = useLocation();
  const presetShortlist = new URLSearchParams(location.search).get('from_shortlist'); // optional deep link
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(!!presetShortlist);
  const [form, setForm] = useState({ name: '', objective: '', budget: '' });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/campaign-plans`, { withCredentials: true });
      setPlans(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load campaign plans');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    if (!name) { toast.error('Plan name is required'); return; }
    setCreating(true);
    try {
      // If creating from a shortlist deep-link, hydrate creators + categories + cities
      // server-side via initial payload — saves the brand a manual step.
      let creator_ids = [];
      let categories = [];
      let target_locations = [];
      let shortlist_id = null;
      if (presetShortlist) {
        try {
          const { data: sl } = await axios.get(`${API_URL}/api/shortlists/${presetShortlist}`, { withCredentials: true });
          creator_ids = sl.creator_ids || [];
          shortlist_id = sl.id;
          categories = sl.stats?.categories || [];
          target_locations = sl.stats?.cities || [];
        } catch { /* non-fatal — plan will be created empty */ }
      }
      const { data } = await axios.post(
        `${API_URL}/api/campaign-plans`,
        {
          name,
          objective: form.objective.trim() || null,
          budget: form.budget ? Number(form.budget) : 0,
          shortlist_id,
          creator_ids,
          categories,
          target_locations,
        },
        { withCredentials: true }
      );
      toast.success('Plan created');
      setOpenCreate(false);
      setForm({ name: '', objective: '', budget: '' });
      // Drop the planner straight onto the new plan's workspace
      window.location.href = `/brand/plans/${data.id}`;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id, e) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm('Delete this campaign plan? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/campaign-plans/${id}`, { withCredentials: true });
      toast.success('Deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]" data-testid="brand-campaign-plans-page">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
            <div>
              <h1 className="text-4xl font-bold tracking-tight text-[#0F0F0F] dark:text-white mb-2 flex items-center gap-3">
                <ClipboardList className="text-[#FF451A]" size={32} />
                Campaign Plans
              </h1>
              <p className="text-[#6B6B6B] dark:text-zinc-400">Plan budgets, deliverables and timelines before submitting a brief.</p>
            </div>
            <Button
              onClick={() => setOpenCreate(true)}
              className="bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-5 py-2.5 font-medium hover:bg-[#E63E17] shadow-[0_4px_20px_rgba(255,69,26,0.3)] flex items-center gap-2"
              data-testid="create-plan-button"
            >
              <Plus size={16} /> New Plan
            </Button>
          </div>

          {loading ? (
            <div className="py-20 text-center"><Loader2 className="inline-block animate-spin text-[#FF451A]" size={28} /></div>
          ) : plans.length === 0 ? (
            <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-16 text-center" data-testid="plans-empty-state">
              <ClipboardList className="mx-auto text-[#6B6B6B] dark:text-zinc-500 mb-3" size={44} />
              <h3 className="text-lg font-semibold text-[#0F0F0F] dark:text-white mb-1">No campaign plans yet</h3>
              <p className="text-sm text-[#6B6B6B] dark:text-zinc-400 mb-5">Create a plan from scratch, or open a shortlist and hit "Build Plan".</p>
              <Button onClick={() => setOpenCreate(true)} className="bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-5 py-2.5 font-medium hover:bg-[#E63E17]">
                <Plus size={14} className="mr-1.5" /> Create your first plan
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="plans-grid">
              {plans.map((p) => {
                const converted = !!p.converted_to_campaign_id;
                return (
                  <Link
                    key={p.id}
                    to={`/brand/plans/${p.id}`}
                    className="group bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-5 hover:border-[#FF451A]/30 hover:-translate-y-[2px] transition-all flex flex-col gap-3"
                    data-testid="plan-card"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-base font-semibold text-[#0F0F0F] dark:text-white truncate">{p.name}</div>
                        {p.objective && <div className="text-xs text-[#6B6B6B] dark:text-zinc-400 mt-0.5 line-clamp-2">{p.objective}</div>}
                      </div>
                      <button onClick={(e) => remove(p.id, e)} className="text-[#6B6B6B] dark:text-zinc-500 hover:text-[#FF3B30] p-1 transition-colors" data-testid="plan-delete-button">
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <div className="flex items-center justify-between text-sm text-zinc-300 mt-auto">
                      <span className="inline-flex items-center gap-1">
                        <IndianRupee size={13} className="text-[#FF451A]" />
                        <span className="font-semibold">{p.budget ? Number(p.budget).toLocaleString('en-IN') : '—'}</span>
                      </span>
                      <span className="text-xs text-[#6B6B6B] dark:text-zinc-500 font-mono">
                        {(p.creator_ids || []).length} creator{(p.creator_ids || []).length === 1 ? '' : 's'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      {converted ? (
                        <span className="text-[10px] uppercase tracking-wider font-mono inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#00C972]/10 text-[#00C972] border border-[#00C972]/20">
                          <FileCheck2 size={10} /> Submitted as brief
                        </span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-full bg-white/5 text-[#6B6B6B] dark:text-zinc-400 border border-black/[0.10] dark:border-white/10">
                          Draft
                        </span>
                      )}
                      <div className="text-[10px] text-[#6B6B6B] dark:text-zinc-500 font-mono">
                        {(() => { try { return `updated ${formatDistanceToNow(new Date(p.updated_at), { addSuffix: true })}`; } catch { return ''; } })()}
                      </div>
                    </div>
                    <div className="text-xs text-[#FF451A] inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      Open workspace <ArrowUpRight size={11} />
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New campaign plan</DialogTitle>
            <DialogDescription className="text-sm text-[#6B6B6B] dark:text-zinc-400">
              Set a name, objective and budget. You can wire deliverables and timeline in the next step.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider block mb-1 text-[#0F0F0F] dark:text-white">Name</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Diwali Festive Drop 2026"
                className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                autoFocus
                data-testid="plan-name-input"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider block mb-1 text-[#0F0F0F] dark:text-white">Objective (optional)</label>
              <Input
                value={form.objective}
                onChange={(e) => setForm({ ...form, objective: e.target.value })}
                placeholder="Drive 5M+ impressions and 50K product page visits"
                className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                data-testid="plan-objective-input"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider block mb-1 text-[#0F0F0F] dark:text-white">Budget (₹, optional)</label>
              <Input
                type="number"
                value={form.budget}
                onChange={(e) => setForm({ ...form, budget: e.target.value })}
                placeholder="500000"
                className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                data-testid="plan-budget-input"
              />
            </div>
            {presetShortlist && (
              <div className="text-[11px] text-[#FF451A] bg-[#FF451A]/10 border border-[#FF451A]/20 rounded-lg px-3 py-2">
                Creators, categories and locations from your shortlist will auto-fill on save.
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <Button
                type="submit"
                disabled={creating}
                className="bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-5 hover:bg-[#E63E17] flex-1 disabled:opacity-40"
                data-testid="plan-create-submit"
              >
                {creating ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Plus size={14} className="mr-1.5" />}
                Create plan
              </Button>
              <Button type="button" onClick={() => setOpenCreate(false)} className="border border-black/[0.10] dark:border-white/10 bg-transparent text-[#0F0F0F] dark:text-white hover:bg-white/5 rounded-full px-5">
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
